// Phase 7 sub-bucket 6 — shared analytics vocabulary. Single source of truth
// for the partner-analytics surface (portal page + admin partner-detail tab).
//
// Previously duplicated across `apps/api/src/services/analytics/{types,
// time-range}.ts` (Phase 4 port) and `/lib/analytics/{types,time-range}.ts`
// (legacy). Hoisted here so SPA + worker share the range vocabulary, KPI
// shapes, and delta math without copy-paste drift.
//
// Helpers are pure (no DOM, no fetch, no Date subclassing) so the same module
// builds in the Hono worker and the Vite SPA.

// ---------------------------------------------------------------------------
// Time range vocabulary
// ---------------------------------------------------------------------------

export const TIME_RANGE_KEYS = ["7d", "30d", "90d", "all"] as const;
export type TimeRangeKey = (typeof TIME_RANGE_KEYS)[number];

export const TIME_RANGE_LABEL: Record<TimeRangeKey, string> = {
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  "90d": "Last 90 days",
  all: "All time",
};

// `from`/`to` bound the current window. `prevFrom`/`prevTo` bound the same
// span shifted back so we can render period-over-period deltas. All four are
// `null` when the range key is "all" — delta math then short-circuits to
// "no comparison available".
export type TimeWindow = {
  key: TimeRangeKey;
  from: Date | null;
  to: Date;
  prevFrom: Date | null;
  prevTo: Date | null;
};

// ---------------------------------------------------------------------------
// Metric / KPI shapes
// ---------------------------------------------------------------------------

export type MetricDelta = {
  current: number;
  previous: number;
  // `null` when previous = 0 (any non-zero current is infinite growth — render
  // as "new" in the UI) or when the window key is "all".
  pct: number | null;
  direction: "up" | "down" | "flat";
};

export type StageBucket = {
  stage_id: string;
  label: string;
  count: number;
};

export type DealPipelineSummary = {
  totals: {
    deals: MetricDelta;
    wins: MetricDelta;
    arr: MetricDelta;
    intros: MetricDelta;
  };
  byStage: StageBucket[];
  // Currency assumption: we filter `deal_amount` to EUR rows when computing
  // ARR so partners aren't shown mixed-currency totals — mirrors
  // /portal/dashboard's `wonValueEur` semantics.
  currency: "EUR";
};

export type TopAsset = {
  asset_id: string;
  title: string;
  asset_type: string;
  views: number;
};

export type ContentEngagementSummary = {
  totals: {
    views: MetricDelta;
    uniqueAssets: MetricDelta;
  };
  topAssets: TopAsset[];
};

export type NotificationEngagementSummary = {
  totals: {
    sent: MetricDelta;
    opened: MetricDelta;
    clicked: MetricDelta;
  };
  openRate: number | null;
  clickRate: number | null;
};

export type TimelineEventKind =
  | "deal_attributed"
  | "deal_won"
  | "content_view"
  | "email_opened";

export type TimelineEvent = {
  id: string;
  kind: TimelineEventKind;
  at: string;
  title: string;
  meta?: string;
  href?: string;
};

export type AnalyticsViewer = "admin" | "partner";

// ---------------------------------------------------------------------------
// Pure helpers (range parsing, delta math, label formatting)
// ---------------------------------------------------------------------------

const DAY = 24 * 60 * 60 * 1000;

const RANGE_DAYS: Record<Exclude<TimeRangeKey, "all">, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

const DEFAULT_KEY: TimeRangeKey = "30d";

// Normalises an arbitrary search-param value (string | string[] | undefined)
// into a known TimeRangeKey. Unknown values fall back to the default so a
// stale link can't break the page.
export function normalizeRangeKey(
  raw: string | string[] | undefined | null,
): TimeRangeKey {
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (!value) return DEFAULT_KEY;
  if ((TIME_RANGE_KEYS as readonly string[]).includes(value)) {
    return value as TimeRangeKey;
  }
  return DEFAULT_KEY;
}

// Builds a TimeWindow with `to = now` and the previous window of equal span
// directly before it. For "all", every bound is `null` — callers should
// short-circuit time filtering and skip delta math.
export function parseRange(
  raw: string | string[] | undefined | null,
): TimeWindow {
  const key = normalizeRangeKey(raw);
  const to = new Date();
  if (key === "all") {
    return { key, from: null, to, prevFrom: null, prevTo: null };
  }
  const days = RANGE_DAYS[key];
  const from = new Date(to.getTime() - days * DAY);
  const prevTo = from;
  const prevFrom = new Date(from.getTime() - days * DAY);
  return { key, from, to, prevFrom, prevTo };
}

// Pure percentage delta. Returns `null` when previous = 0 — the UI renders
// that as "new" (or just hides the delta) rather than infinite growth.
export function pctDelta(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return ((current - previous) / previous) * 100;
}

export function buildDelta(current: number, previous: number): MetricDelta {
  const pct = pctDelta(current, previous);
  let direction: MetricDelta["direction"] = "flat";
  if (current > previous) direction = "up";
  else if (current < previous) direction = "down";
  return { current, previous, pct, direction };
}

// "+12.3%" / "−4.0%" / "—" for the KPI delta badge.
export function formatDeltaPct(delta: MetricDelta): string {
  if (delta.pct === null) {
    return delta.current === 0 ? "—" : "New";
  }
  const sign = delta.pct > 0 ? "+" : delta.pct < 0 ? "−" : "";
  const abs = Math.abs(delta.pct);
  // One decimal for small movements, zero for double-digit so badges stay
  // narrow on mobile.
  const formatted = abs < 10 ? abs.toFixed(1) : abs.toFixed(0);
  return `${sign}${formatted}%`;
}

// Build a `?range=` URL string preserving every other search param. Used by
// callers that wire the selector to URL state — pure helper so it's testable
// without a router.
export function buildRangeHref(
  pathname: string,
  currentParams: URLSearchParams,
  nextRange: TimeRangeKey,
): string {
  const params = new URLSearchParams(currentParams);
  params.set("range", nextRange);
  return `${pathname}?${params.toString()}`;
}
